import * as R from 'ramda';
import { getOrderProductUniqueIdentifier } from '../../../utils/getOrderProductUniqueIdentifier';
import SearchStringUtil from '../../../utils/searchString.utils';
import { AvailabilityEnum, PriceValues } from '../../products/ProductTypes';
import { isPreOrderOrMaterial } from '../../products/utils/availability.utils';
import { OnGridValueChange } from './interfaces/on-grid-value-change.interface';
import {
  CalculateProductSectionIndex,
  GetOrderProductSelectionEdge,
  GetProductSectionIndex,
  OrderHeadersGroupByInfo,
  OrderProduct,
  OrderProductGridItem,
  OrderProductSelectionEdge,
  Section,
  UpdateItemFormat,
} from './orderProducts.types';

const sumSectionLength = (sum: number, sectionLength: number) => sectionLength + sum;

export const calculateProductSectionIndex = ({
  sectionLengths,
  productIndex,
  sectionIndex,
}: CalculateProductSectionIndex) => {
  const slicedSections = sectionLengths.slice(0, sectionIndex);

  if (sectionIndex <= 0) {
    return productIndex;
  }

  return productIndex - slicedSections.reduce(sumSectionLength, 0);
};

export const getProductSectionIndex = ({
  productIndex,
  sectionIndex,
  sections,
}: GetProductSectionIndex) => {
  const sectionLengths = sections.map((section) =>
    section.isExpanded ? section.products.length : 0,
  );
  return calculateProductSectionIndex({ productIndex, sectionIndex, sectionLengths });
};

export const isAllPricesEqual = (prices: Array<PriceValues>) => {
  if (R.isEmpty(prices)) {
    return false;
  }

  const isAllWholesalePriceEqual = prices.every(
    (price) => price.wholesalePrice === prices[0].wholesalePrice,
  );
  const isAllPromotionalPriceEqual = prices.every(
    (price) => price.promotionalPrice === prices[0].promotionalPrice,
  );

  return isAllWholesalePriceEqual && isAllPromotionalPriceEqual;
};

const getSizeBySku = (grid: OrderProductGridItem[], skuId?: string) =>
  grid.find((item) => item.skuId === skuId);

export const calculateTotalAddOrderProduct = (
  updateItems: UpdateItemFormat[],
  grid: OrderProductGridItem[],
) =>
  updateItems.reduce((prev, cur) => {
    const gridItem = getSizeBySku(grid, cur.skuId);
    const price = gridItem ? (gridItem.orderPromotionalPrice ?? gridItem.orderWholeSalePrice) : 0;
    return prev + R.multiply(cur.quantity || 0, price);
  }, 0);

export const calculateTotalOrderProduct = (
  grid?: Pick<
    OrderProductGridItem,
    'orderPromotionalPrice' | 'orderWholeSalePrice' | 'orderItemQuantity'
  >[],
) => {
  if (R.isNil(grid) || R.isEmpty(grid)) {
    return 0;
  }
  return grid.reduce((prev, cur) => {
    const price = cur.orderPromotionalPrice ?? cur.orderWholeSalePrice ?? 0;
    return prev + R.multiply(cur.orderItemQuantity || 0, price);
  }, 0);
};

export const calculateProductTotalParts = (
  grid: Pick<OrderProductGridItem, 'orderItemQuantity'>[],
): number => grid.reduce((prev, cur) => prev + (cur.orderItemQuantity || 0), 0);

export const isSameOrderProduct = (
  product1: Partial<OrderProduct>,
  product2: Partial<OrderProduct>,
) =>
  product1.productColorId === product2.productColorId &&
  product1.productId === product2.productId &&
  product1.availability === product2.availability &&
  product1.deliveryWindow?.id === product2.deliveryWindow?.id;
export const filterProductsAndRemove = (products: OrderProduct[], product: OrderProduct) =>
  products.filter((item) => !isSameOrderProduct(product, item));

export const getOrderProductsGroupCount = (sections: Section[]): number[] => {
  const groupCount = sections?.reduce((current, section) => {
    if (section.isExpanded) {
      return [...current, section.productsCount];
    }
    return [...current, 0];
  }, [] as number[]);
  return groupCount;
};

export const getOrderProductsGroupSectionIndexes = (
  sections: Section[] = [],
  isGroupedView: boolean,
): number[] => {
  if (!isGroupedView) {
    return [];
  }

  return sections.reduce((current, section, sectionIndex) => {
    if (sectionIndex === 0 || !section.products.length) {
      return current;
    }
    return [...current, sectionIndex];
  }, [] as number[]);
};

export const getOrderProductsGroupStartIndexes = (groupCounts: number[]) => {
  const startIndexes = new Set<number>();
  let firstIndex = 0;

  groupCounts.forEach((count) => {
    startIndexes.add(firstIndex);
    firstIndex += count;
  });

  return startIndexes;
};

export const getOrderProductSelectionEdge = ({
  productIndex,
  orderProducts,
  selectedItems,
  groupStartIndexes,
}: GetOrderProductSelectionEdge): OrderProductSelectionEdge => {
  const isSelectedAt = (index: number) => {
    const product = orderProducts[index];
    return !!product && selectedItems.has(getOrderProductUniqueIdentifier(product));
  };

  if (!isSelectedAt(productIndex)) {
    return 'none';
  }

  const isRunStart = groupStartIndexes.has(productIndex) || !isSelectedAt(productIndex - 1);
  const isRunEnd = groupStartIndexes.has(productIndex + 1) || !isSelectedAt(productIndex + 1);

  if (isRunStart && isRunEnd) {
    return 'single';
  }

  if (isRunStart) {
    return 'start';
  }

  if (isRunEnd) {
    return 'end';
  }

  return 'middle';
};

const matchesOrderProductSearch = (product: OrderProduct, normalizedSearch: string) =>
  SearchStringUtil.normalize(product.name).includes(normalizedSearch) ||
  SearchStringUtil.normalize(product.code).includes(normalizedSearch) ||
  SearchStringUtil.normalize(product.referenceCode ?? '').includes(normalizedSearch);

export const filterSectionsByOrderProductSearch = (
  sections: Section[] = [],
  searchText: string,
): Section[] => {
  const normalizedSearch = SearchStringUtil.normalize(searchText);

  if (!normalizedSearch) {
    return sections;
  }

  return sections.map((section) => {
    const filteredProducts = section.products.filter((product) =>
      matchesOrderProductSearch(product, normalizedSearch),
    );

    return {
      ...section,
      products: filteredProducts,
      productsCount: filteredProducts.length,
      isExpanded: filteredProducts.length > 0,
    };
  });
};

export const filterSectionsBySelectedCondition = (
  sections: Section[] = [],
  selectedConditionId?: string,
): Section[] => {
  if (!selectedConditionId) {
    return sections;
  }

  return sections.map((section) => {
    const filteredProducts = section.products.filter(
      (product) => product.orderConditionId === selectedConditionId,
    );

    return {
      ...section,
      products: filteredProducts,
      productsCount: filteredProducts.length,
      isExpanded: filteredProducts.length > 0,
    };
  });
};

export const getOrderProductsFilteredBySections = (sections: Section[] = []): OrderProduct[] => {
  const newProducts = sections.reduce((current, section) => {
    if (section.isExpanded) {
      const products = [...current, ...section.products];
      return products;
    }
    return current;
  }, [] as OrderProduct[]);

  return newProducts;
};

export const getGroupByInfo = (value?: string | null): OrderHeadersGroupByInfo | null => {
  if (!value) {
    return null;
  }

  return JSON.parse(value);
};

export const buildParamsToUpdateItemFormat = (
  params: OnGridValueChange,
  availability: AvailabilityEnum,
): UpdateItemFormat => {
  if (params.orderItemId) {
    return {
      availability,
      deliveryWindowId: params.deliveryWindowId,
      id: params.orderItemId,
      orderConditionId: params.orderConditionId,
      quantity: params.value,
      skuId: params.skuId,
    };
  }

  return {
    availability,
    deliveryWindowId: params.deliveryWindowId,
    orderConditionId: params.orderConditionId,
    quantity: params.value,
    skuId: params.skuId,
  };
};

export const getAvailabilityColor = (
  availability: AvailabilityEnum,
): 'primary' | 'secondary' | undefined => {
  if (availability === AvailabilityEnum.EMPTY) {
    return;
  }

  if (isPreOrderOrMaterial(availability)) {
    return 'secondary';
  }

  return 'primary';
};

export type OrderProductsEmptyStateKind = 'order-empty' | 'filter-empty' | null;

interface ResolveOrderProductsEmptyStateParams {
  totalProductsCount: number;
  sectionsCount: number;
  visibleProductsCount: number;
  hasActiveFilter: boolean;
}

export const resolveOrderProductsEmptyStateKind = ({
  totalProductsCount,
  sectionsCount,
  visibleProductsCount,
  hasActiveFilter,
}: ResolveOrderProductsEmptyStateParams): OrderProductsEmptyStateKind => {
  if (totalProductsCount <= 0 && sectionsCount <= 1) {
    return 'order-empty';
  }

  if (hasActiveFilter && visibleProductsCount <= 0) {
    return 'filter-empty';
  }

  return null;
};
